import { Button, StatusBadge } from '@argink/ui';
import type { AgentCert } from '../lib/agent';
import type { HumanError } from '../lib/errors';
import { formatDate, isCertExpired, sourceLabel } from '../lib/format';
import { certHolderName, issuerLabel } from '../lib/cpf';
import { StateCard } from './StateCard';
import { CertListSkeleton } from './Skeleton';

export interface CertListProps {
  /** Certificados JÁ FILTRADOS pela expectativa de CPF (#92) — um certificado de
   * titular incompatível NUNCA chega a esta lista (nem desabilitado); o filtro
   * vive em `lib/cpf.ts` + SignPage, não aqui. */
  certs: AgentCert[] | null;
  loading: boolean;
  error: HumanError | null;
  selectedKeyId: string | null;
  onSelect: (cert: AgentCert) => void;
  onRetry: () => void;
  /** CPF mascarado do PRÓPRIO signatário (certificate-expectation.masked_cpf) —
   * usado SÓ na mensagem de lista vazia quando o filtro está ativo, para
   * orientar qual certificado procurar. `null` = filtro inativo (mensagem
   * genérica de sempre). */
  expectedMaskedCpf?: string | null;
}

/**
 * Lista de certificados devolvida pelo agente local — só METADADO legível
 * (titular sem CPF, emissor, validade, fonte A1/A3; o DN cru RFC2253 não é mais
 * exibido — D-063 item 2). Nunca há campo de upload de arquivo .p12/.pfx nem
 * entrada de PIN/senha aqui: a posse/uso da chave é inteiramente do agente
 * local (D-001, zero custódia) — esta tela só escolhe QUAL certificado já
 * disponível na máquina será usado.
 */
export function CertList({ certs, loading, error, selectedKeyId, onSelect, onRetry, expectedMaskedCpf = null }: CertListProps) {
  if (loading) return <CertListSkeleton />;

  if (error) {
    return (
      <StateCard
        tone="error"
        title="Não foi possível listar seus certificados"
        detail={error.detail}
        traceId={error.traceId}
        action={
          <Button variant="outline" onClick={onRetry}>
            Tentar de novo
          </Button>
        }
      />
    );
  }

  if (!certs || certs.length === 0) {
    return (
      <StateCard
        tone="empty"
        title="Nenhum certificado encontrado"
        detail={
          expectedMaskedCpf
            ? `O agente local está rodando, mas nenhum certificado instalado nesta máquina pertence ao CPF ${expectedMaskedCpf} convidado para esta assinatura. Confira se o seu e-CPF (A1 .p12/.pfx ou A3 token/smartcard) está instalado nesta máquina.`
            : 'O agente local está rodando, mas não achou nenhum certificado A1 (.p12/.pfx) nem A3 (token/smartcard) instalado nesta máquina.'
        }
        action={
          <Button variant="outline" onClick={onRetry}>
            Procurar de novo
          </Button>
        }
      />
    );
  }

  return (
    <div className="cert-list" role="group" aria-label="Escolha o certificado para assinar">
      {certs.map((cert) => {
        // D-034: vencido é só um AVISO preventivo (badge + hint) — quem barra de
        // verdade é o servidor (422 certificate_expired na pré-checagem, ver
        // lib/errors.ts). A UI não decide pelo usuário nem esconde o certificado:
        // ele continua selecionável, igual a qualquer outro.
        const expired = isCertExpired(cert.notAfter);
        const selected = cert.keyId === selectedKeyId;
        const holder = certHolderName(cert.subject) ?? certHolderName(`CN=${cert.label}`) ?? cert.label;
        return (
          <button
            key={cert.keyId}
            type="button"
            className={`cert-card argink-card argink-card--default argink-focusable${selected ? ' cert-card--selected' : ''}${expired ? ' cert-card--not-recommended' : ''}`}
            aria-pressed={selected}
            aria-label={expired ? `${holder} — certificado vencido, não recomendado` : holder}
            onClick={() => onSelect(cert)}
          >
            <div className="cert-card__header">
              <StatusBadge tone="info">{sourceLabel(cert.source)}</StatusBadge>
              {expired ? <StatusBadge tone="warning">Vencido</StatusBadge> : null}
            </div>
            <p className="cert-card__label">{holder}</p>
            <dl className="kv-grid kv-grid--tight">
              <div className="kv-grid__item kv-grid__item--full">
                <dt>Emissor</dt>
                <dd>{issuerLabel(cert.issuer)}</dd>
              </div>
              <div className="kv-grid__item">
                <dt>Válido até</dt>
                <dd>{formatDate(cert.notAfter)}</dd>
              </div>
            </dl>
            {expired ? (
              <p className="cert-card__hint">Vencido — o servidor pode recusar a assinatura com este certificado.</p>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
